export type LogEntry = {
    timestamp?: string;
    level?: string;
    source?: string;
    event: string;
};

export type LogItem =
    | { kind: 'line'; entry: LogEntry; groupId: string | null }
    | { kind: 'group-start'; name: string; groupId: string }
    | { kind: 'group-end'; groupId: string };

const PLAIN_TEXT_LINE_RE = /^\[(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(?:[,.]\d+)?)\]\s+\{([^}]*)\}\s+(\w+)\s+-\s+(.*)$/;

const GROUP_START_RE = /::group::(.*)$/;
const GROUP_END_RE = /::endgroup::/;

/*
 * Parses raw NDJSON log lines (one JSON object per line, Airflow 2.x format), skipping
 * any line that fails to parse as an object
 */
function parseNdjson(content: string): LogEntry[] {
    return content
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
            try {
                const parsed = JSON.parse(line);
                if (!parsed || typeof parsed !== 'object') return null;
                return {
                    timestamp: parsed.timestamp ?? parsed.asctime ?? parsed.ts,
                    level: parsed.level ?? parsed.levelname,
                    source: parsed.logger ?? parsed.name ?? parsed.source,
                    event: parsed.event ?? parsed.message ?? parsed.msg ?? JSON.stringify(parsed),
                } as LogEntry;
            } catch {
                return null;
            }
        })
        .filter((entry): entry is LogEntry => entry !== null);
}

/*
 * Parses Airflow 1.x plain-text logs ("[timestamp] {file:line} LEVEL - message"),
 * folding non-matching lines (e.g. multi-line stack traces) into the previous entry
 */
function parsePlainText(content: string): LogEntry[] {
    const entries: LogEntry[] = [];
    content.split('\n').forEach((line) => {
        const match = line.match(PLAIN_TEXT_LINE_RE);
        if (match) {
            const [, timestamp, source, level, message] = match;
            entries.push({ timestamp, source, level, event: message });
            return;
        }
        if (line.trim() === '') return;
        const previous = entries[entries.length - 1];
        if (previous) {
            previous.event += `\n${line}`;
        } else {
            entries.push({ event: line });
        }
    });
    return entries;
}

/*
 * Builds nested group-start/group-end/line items from a flat entry list, detecting
 * GitHub-Actions-style "::group::name" / "::endgroup::" markers embedded in the event text
 */
function parseEntries(entries: LogEntry[]): LogItem[] {
    const items: LogItem[] = [];
    const groupStack: string[] = [];
    let nextGroupId = 0;

    entries.forEach((entry) => {
        const groupStartMatch = entry.event.match(GROUP_START_RE);
        if (groupStartMatch) {
            const groupId = `group-${nextGroupId++}`;
            items.push({ kind: 'group-start', name: groupStartMatch[1].trim(), groupId });
            groupStack.push(groupId);
            return;
        }
        if (GROUP_END_RE.test(entry.event)) {
            const groupId = groupStack.pop();
            if (groupId) items.push({ kind: 'group-end', groupId });
            return;
        }
        const groupId = groupStack.length > 0 ? groupStack[groupStack.length - 1] : null;
        items.push({ kind: 'line', entry, groupId });
    });

    return items;
}

/*
 * Parses task instance log content into a flat, grouped item list, auto-detecting
 * whether the payload is an entry array, NDJSON (Airflow 2.x), or plain text (Airflow 1.x)
 */
export function parseLogContent(rawContent: string | LogEntry[]): LogItem[] {
    if (Array.isArray(rawContent)) {
        return parseEntries(rawContent);
    }

    const nonEmptyLines = rawContent
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .slice(0, 2);

    const looksLikeNdjson =
        nonEmptyLines.length > 0 &&
        nonEmptyLines.every((line) => {
            try {
                const parsed = JSON.parse(line);
                return parsed !== null && typeof parsed === 'object';
            } catch {
                return false;
            }
        });

    const entries = looksLikeNdjson ? parseNdjson(rawContent) : parsePlainText(rawContent);
    return parseEntries(entries);
}

/*
 * Reconstructs plain-text log content from parsed items, for copy/download actions
 */
export function itemsToText(items: LogItem[]): string {
    return items
        .filter((item): item is Extract<LogItem, { kind: 'line' }> => item.kind === 'line')
        .map(({ entry }) => `[${entry.timestamp ?? ''}] ${entry.level ?? ''} - ${entry.event}`.trim())
        .join('\n');
}
